<script setup lang="ts">
import { computed } from 'vue'

import { useReportMedia } from '../../../composables/useReportMedia'
import type { ReportRow } from '../../../models/report.types'

import { useI18n } from '@/shared/i18n'
import { Icon } from '@/shared/ui/icon'
import { MediaViewer, PreviewImage, type MediaItem } from '@/shared/ui/media'

const props = withDefaults(
  defineProps<{
    row: ReportRow
    alt: string
    mediaType?: 'Image' | 'Video'
  }>(),
  { mediaType: 'Image' },
)

const { t } = useI18n()
const {
  assets,
  active,
  activeIndex,
  previous,
  next,
  broken,
  markBroken,
  viewerOpen,
  openViewer,
  closeViewer,
} = useReportMedia(
  () => props.row,
  () => (props.mediaType === 'Video' ? 'video' : 'image'),
)

const isGallery = computed(() => assets.value.length > 1)
const viewerItems = computed<MediaItem[]>(() =>
  assets.value.map((asset) => ({
    src: asset.src,
    type: asset.kind,
    alt: props.alt,
    thumbnail: asset.thumbnail,
  })),
)
const thumbnail = computed(() => active.value?.thumbnail || active.value?.src || '')
</script>

<template>
  <div class="report-media-gallery grid justify-items-center gap-xs">
    <button
      v-if="thumbnail && !broken"
      type="button"
      class="report-media-gallery__thumb group relative inline-flex w-14 h-14 items-center justify-center overflow-hidden p-0 border border-border rounded-sm bg-surface cursor-zoom-in focus-visible:outline-[length:var(--ui-ring-width)] focus-visible:outline-solid focus-visible:outline-ring focus-visible:outline-offset-2"
      :aria-label="t('reports.table.openMediaPreview', { value: alt })"
      @click="openViewer"
    >
      <img
        class="w-full h-full object-contain"
        :src="thumbnail"
        :alt="alt"
        loading="lazy"
        @error="markBroken"
      />
      <span
        class="absolute inset-0 flex items-center justify-center bg-primary/70 text-primary-fg opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
        data-testid="report-table-media-hover"
        aria-hidden="true"
      >
        <Icon :name="active?.kind === 'video' ? 'video' : 'image'" :size="16" />
      </span>
    </button>
    <span v-else class="report-media-gallery__unavailable text-muted text-xs">
      {{ t('reports.table.mediaUnavailable') }}
    </span>

    <div v-if="isGallery" class="report-media-gallery__nav flex items-center gap-xs">
      <button
        type="button"
        class="report-media-gallery__step grid w-5 h-5 place-items-center p-0 border-0 rounded-full bg-primary text-primary-fg cursor-pointer [&:disabled]:cursor-default [&:disabled]:opacity-50"
        :disabled="activeIndex === 0"
        :aria-label="t('reports.card.previous')"
        data-testid="report-table-media-previous"
        @click.stop="previous"
      >
        <Icon name="chevron-left" :size="10" aria-hidden="true" />
      </button>
      <button
        type="button"
        class="report-media-gallery__step grid w-5 h-5 place-items-center p-0 border-0 rounded-full bg-primary text-primary-fg cursor-pointer [&:disabled]:cursor-default [&:disabled]:opacity-50"
        :disabled="activeIndex === assets.length - 1"
        :aria-label="t('reports.card.next')"
        data-testid="report-table-media-next"
        @click.stop="next"
      >
        <Icon name="chevron-right" :size="10" aria-hidden="true" />
      </button>
    </div>

    <MediaViewer
      v-if="viewerOpen && isGallery"
      :items="viewerItems"
      :index="activeIndex"
      :close-label="t('reports.actions.close')"
      :prev-label="t('reports.card.previous')"
      :next-label="t('reports.card.next')"
      :retry-label="t('common.retry')"
      :load-error-message="t('reports.table.mediaUnavailable')"
      @close="closeViewer"
    />
    <PreviewImage
      v-else-if="thumbnail"
      v-model="viewerOpen"
      :src="thumbnail"
      :alt="alt"
      :rotate-left-label="t('reports.card.rotateLeft')"
      :rotate-right-label="t('reports.card.rotateRight')"
      :zoom-in-label="t('reports.card.zoomIn')"
      :zoom-out-label="t('reports.card.zoomOut')"
      :close-label="t('reports.actions.close')"
    />
  </div>
</template>
